/**
 * L'interpolation des champs liés, côté client (plan media-template-generators §4) — le
 * miroir de StringInterpolator (Ruby) : les deux passent les mêmes cas,
 * test/fixtures/files/interpolation_cases.json.
 *
 * Le document stocke des TOKENS, jamais leur valeur : ce que le texte rend dépend de qui
 * regarde — l'admin qui a lié la variable, l'affilié qui consulte, la company qui publie.
 * La résolution appartient donc à l'affichage, avec les valeurs du contexte courant.
 *
 * Le langage complet : `$TOKEN` et sa chaîne d'opérations (lib variable_chain), les blocs
 * conditionnels `?{(condition) texte}` (lib condition, imbricables — un bloc masqué ne
 * laisse pas de ligne vide), `ALEA{a | b}` (le serveur tire au sort ; l'aperçu montre le
 * premier). Ce que le client ne sait pas : les masques de traduction des enums.
 *
 * `keepUnknown` distingue les deux publics : l'éditeur montre les trous (un token sans
 * valeur reste visible, chaîne comprise — c'est un outil), les previews de lecture imitent
 * le rendu publié (un token sans valeur disparaît).
 *
 * Deux sortes de champs liés : le texte, où un token reste montrable ; une source d'image
 * (`image.src`, le média dynamique : la photo de la company à la place de l'exemple de
 * l'admin), où rien d'autre qu'une url complète ne s'affiche — tant que l'expression n'est
 * pas entièrement résolue, l'exemple stocké reste en place, pour tous les publics.
 *
 * L'interpolation rend aussi LA CARTE DES SEGMENTS (plan bound-text-styles §3.1) : la
 * correspondance entre les plages de l'expression et celles du résultat — ce qui permet
 * de transposer les styles par caractère d'un texte lié (lib style_transposition). Les
 * index sont ceux des styles sérialisés de Fabric : graphèmes, retours à la ligne exclus.
 */
import { layoutDescendants, layoutParents } from "@frabr/fabric-editor";
import { util } from "fabric";
import { evaluateCondition } from "./condition";
import { looksLikeColor } from "./fill_cases";
import { transposeStyles } from "./style_transposition";
import { chained, lookup, VARIABLE_SOURCE } from "./variable_chain";

const BARE_TOKEN = /\$\w+/;

/** Une expression porte-t-elle une variable ? Sans variable, ce n'est pas une liaison : c'est
 *  du texte (règle du panneau Variables et de la textbox). */
export function hasVariable(expression: string): boolean {
  return BARE_TOKEN.test(expression);
}
const SLOT = /^media\[/;
/** Dans une partie fixe : un `ALEA{a | b}` (groupe 1), ou un token et sa chaîne (groupes 2, 3). */
const INLINE = new RegExp(`ALEA\\{([\\s\\S]*?)\\}|${VARIABLE_SOURCE}`, "g");

export interface BindingSpec {
  expr?: string;
  scope?: string;
  resolved?: boolean;
  [key: string]: unknown;
}

export interface BoundLayer {
  bindings?: Record<string, BindingSpec | undefined>;
  [key: string]: unknown;
}

export interface InterpolateOptions {
  keepUnknown?: boolean;
}

/**
 * Un segment de la carte : `from` dans l'expression, `to` dans le résultat (bornes
 * [début, fin)). `text` : une partie fixe copiée telle quelle ; `token` : `$NOM` et sa
 * chaîne → sa valeur ; `block` : un bloc `?{(…) …}` ou `ALEA{…}` → son résultat, atomique.
 */
export interface Segment {
  kind: "text" | "token" | "block";
  from: [number, number];
  to: [number, number];
}

export interface Interpolation {
  text: string;
  segments: Segment[];
  /** La même carte en offsets de chaîne (retours à la ligne compris) : ce qu'il faut pour
   *  reporter une frappe dans l'expression — un retour à la ligne n'a pas d'index plat. */
  units: Segment[];
}

export function interpolateExpression(
  expression: string,
  values: Record<string, unknown> = {},
  options: InterpolateOptions = {},
): string {
  return interpolateWithMap(expression, values, options).text;
}

/** L'interpolation et sa carte des segments. */
export function interpolateWithMap(
  expression: string,
  values: Record<string, unknown> = {},
  options: InterpolateOptions = {},
): Interpolation {
  const source = String(expression ?? "");
  const out = new Builder(source);
  if (source) processMessage(out, 0, source.length, values, options);
  return out.result();
}

/** Les positions d'un texte en index plats de graphèmes, retours à la ligne exclus — la
 *  convention des styles sérialisés de Fabric (stylesToArray). */
export function flatIndexes(text: string): (offset: number) => number {
  const at = new Map<number, number>();
  let offset = 0;
  let flat = 0;
  for (const grapheme of graphemes(text)) {
    at.set(offset, flat);
    offset += grapheme.length;
    if (grapheme !== "\n") flat++;
  }
  at.set(offset, flat);
  return (o) => {
    while (o > 0 && !at.has(o)) o--;
    return at.get(o) ?? 0;
  };
}

/** Le découpage de Fabric lui-même : les index de la carte sont ceux de ses styles. */
function graphemes(text: string): string[] {
  return util.string.graphemeSplit(text);
}

/** Construit le résultat et sa carte, en offsets de chaîne — convertis en index plats à la fin. */
class Builder {
  text = "";
  private readonly raw: Segment[] = [];

  constructor(readonly source: string) {}

  /** Une partie fixe de l'expression, copiée. */
  copy(from: number, to: number): void {
    if (to <= from) return;
    this.push("text", from, to, this.source.slice(from, to));
  }

  /** Une plage de l'expression remplacée par une valeur. */
  emit(kind: "token" | "block", from: number, to: number, value: string): void {
    this.push(kind, from, to, value);
  }

  private push(kind: Segment["kind"], from: number, to: number, value: string): void {
    const start = this.text.length;
    this.text += value;
    this.raw.push({ kind, from: [from, to], to: [start, this.text.length] });
  }

  result(): Interpolation {
    const inSource = flatIndexes(this.source);
    const inText = flatIndexes(this.text);
    const segments: Segment[] = [];
    for (const s of this.raw) {
      const seg: Segment = { kind: s.kind, from: [inSource(s.from[0]), inSource(s.from[1])], to: [inText(s.to[0]), inText(s.to[1])] };
      const last = segments[segments.length - 1];
      if (last && last.kind === "text" && seg.kind === "text" && last.from[1] === seg.from[0] && last.to[1] === seg.to[0]) {
        last.from[1] = seg.from[1];
        last.to[1] = seg.to[1];
      } else if (seg.kind !== "text" || seg.from[1] > seg.from[0]) {
        segments.push(seg);
      }
    }
    return { text: this.text, segments, units: this.raw.map((r) => ({ ...r, from: [...r.from], to: [...r.to] })) };
  }
}

/* ---------- les blocs ?{(condition) texte} ---------- */

/** `[start, end)` de la source : parties fixes et tokens, blocs conditionnels évalués. */
function processMessage(out: Builder, start: number, end: number, values: Record<string, unknown>, options: InterpolateOptions): void {
  const { source } = out;
  let pos = start;
  while (pos < end) {
    const match = /(^|\n)?\?\{/.exec(source.slice(pos, end));
    if (!match) {
      interpolateVariables(out, pos, end, values, options);
      return;
    }
    const blockAt = pos + match.index;
    if (match.index > 0) interpolateVariables(out, pos, blockAt, values, options);
    const lead = match[1] || "";
    const open = blockAt + lead.length;
    const close = matchingBlockEnd(source.slice(open, end));
    if (close === null) {
      // Bloc jamais fermé : le reste est du texte (le serveur, lui, refuse l'expression)
      interpolateVariables(out, blockAt, end, values, options);
      return;
    }
    if (lead) out.copy(blockAt, open);
    let after = open + close;
    let trailing = "";
    if (after < end && source[after] === "\n") {
      after++;
      trailing = "\n";
    }
    const result = processConditionalBlock(source.slice(open, open + close), values, options);
    out.emit("block", open, after, result + (result ? trailing : ""));
    pos = after;
  }
}

/** La fin d'un bloc ouvert en tête (`?{`), accolades imbriquées comprises ; null sans fermeture. */
function matchingBlockEnd(text: string): number | null {
  let depth = 1;
  let pos = 2;
  while (depth > 0 && pos < text.length) {
    if (text.startsWith("?{", pos)) {
      depth++;
      pos += 2;
    } else {
      if (text[pos] === "}") depth--;
      pos++;
    }
  }
  return depth > 0 ? null : pos;
}

function processConditionalBlock(block: string, values: Record<string, unknown>, options: InterpolateOptions): string {
  const split = splitBlock(block);
  if (!split) return "";
  const [condition, content] = split;
  return evaluateCondition(condition, values) ? interpolateWithMap(content.trim(), values, options).text : "";
}

/** `?{(condition) contenu}` → [condition, contenu] : la condition va jusqu'à SA parenthèse
 *  fermante (profondeur comptée), pas la première venue. */
export function splitBlock(block: string): [string, string] | null {
  if (!block.startsWith("?{(") || !block.endsWith("}")) return null;
  let depth = 0;
  for (let i = 2; i < block.length - 1; i++) {
    if (block[i] === "(") depth++;
    else if (block[i] === ")" && --depth === 0) return [block.slice(3, i), block.slice(i + 1, -1)];
  }
  return null;
}

/* ---------- ALEA et variables ---------- */

/** `[start, end)` de la source, sans bloc : texte copié, `ALEA{a | b}` (l'aperçu montre le
 *  premier ; le serveur tire au sort) et tokens remplacés. */
function interpolateVariables(
  out: Builder,
  start: number,
  end: number,
  values: Record<string, unknown>,
  { keepUnknown = false }: InterpolateOptions,
): void {
  const { source } = out;
  let last = start;
  for (const m of source.slice(start, end).matchAll(INLINE)) {
    const from = start + (m.index ?? 0);
    const to = from + m[0].length;
    out.copy(last, from);
    if (m[1] !== undefined) {
      out.emit("block", from, to, interpolateWithMap(m[1].split("|")[0].trim(), values, { keepUnknown }).text);
    } else {
      const value = lookup(values, m[2]);
      let rendered: string;
      if (value === undefined || value === null) rendered = keepUnknown ? m[0] : "";
      else rendered = m[3] ? chained(String(value), m[3]) : String(value);
      out.emit("token", from, to, rendered);
    }
    last = to;
  }
  out.copy(last, end);
}

/* ---------- les champs liés ---------- */

/** Un binding `resolved` est une provenance, plus un token vivant : sa valeur a été posée
 *  à l'expansion (le sujet est consommé), on ne ré-évalue JAMAIS son expression — elle
 *  effacerait la valeur du sujet avec les variables d'un autre scope. */
export function liveExpression(spec: BindingSpec | undefined): string | null {
  return spec?.expr && spec.resolved !== true ? spec.expr : null;
}

/** Des layers avec au moins un champ lié vivant : ils s'interpolent à l'affichage. */
export function hasLiveBindings(layers: object[]): boolean {
  return layers.some((layer) =>
    Object.values((layer as BoundLayer).bindings ?? {}).some((spec) => liveExpression(spec) !== null));
}

/** L'url d'une source liée pour ces valeurs — null tant qu'un token reste sans valeur, et
 *  null pour un slot (`media[i]`) : seule l'expansion le lie, l'exemple reste à l'affichage. */
export function resolvedSource(expr: string, values: Record<string, unknown> = {}): string | null {
  if (SLOT.test(expr)) return null;
  const url = interpolateExpression(expr, values, { keepUnknown: true });
  return url && !hasVariable(url) ? url : null;
}

/** Les layers d'une page sans ceux qu'une condition de visibilité masque pour ces valeurs —
 *  ni leurs descendants de mise en page, comme l'expansion les retire (plan §4.4). Un aperçu
 *  imite le rendu : le fantôme à 22 % est une vue d'auteur, réservée à l'éditeur. */
export function withoutHiddenLayers(layers: object[], values: Record<string, unknown> = {}): object[] {
  const off = new Set<string>();
  layers.forEach((layer) => {
    const bound = layer as BoundLayer;
    const code = liveExpression(bound.bindings?.visible);
    if (code !== null && !evaluateCondition(code, values)) off.add(String(bound.layerId));
  });
  if (off.size === 0) return layers;

  const hidden = new Set([...off, ...layoutDescendants(layoutParents(layers as never), off)]);
  return layers.filter((layer) => !hidden.has(String((layer as BoundLayer).layerId)));
}

/** Les layers d'une page, champs liés résolus — sans toucher aux layers d'origine. */
export function interpolateLayers(
  layers: object[],
  values: Record<string, unknown> = {},
  options: InterpolateOptions = {},
): object[] {
  return layers.map((layer) => {
    const bindings = (layer as BoundLayer).bindings;
    if (!bindings) return layer;

    let out = layer as BoundLayer;
    for (const [field, spec] of Object.entries(bindings)) {
      const expr = liveExpression(spec);
      if (expr === null) continue;

      if (field === "text") {
        // Les styles par caractère suivent le texte (plan bound-text-styles §3.3)
        const { text, segments } = interpolateWithMap(expr, values, options);
        out = { ...out, text };
        if (Array.isArray(out.styles)) out = { ...out, styles: transposeStyles(out.styles as never, segments) };
      }
      else if (field === "image.src") {
        const src = resolvedSource(expr, values);
        if (src) out = { ...out, image: { ...(out.image as object), src } };
      } else if (field === "fill") {
        // La couleur selon l'annonce : une preview la montre comme l'éditeur
        const fill = interpolateExpression(expr, values);
        if (looksLikeColor(fill)) out = { ...out, fill };
      }
    }
    return out;
  });
}
